import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, ViewChildren, QueryList, ElementRef, HostListener } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Emittable, Emitter } from '@ngxs-labs/emitter';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { FeedbackState } from './../../secure/feedback/feedback.state';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AiPredictionBox, Annotation, DrawingTool, LabelStateFormValue, LabelStateV1 } from './annotation.types';
import { getAiPredictionBoxes } from './ai-prediction-overlay.util';
import { ModalMouseHandlers } from './modal.mouse-handlers';
import { ModalImageLoader } from './modal.image-loader';
import { ModalCanvasRenderer } from './modal.canvas-renderer';
import { ModalClassManager } from './modal.class-manager';
import { ModalLabelState } from './modal.label-state';
import { clearLabelsState } from './clear-labels.util';
import { buildModalInfoFields } from './modal.field-builder';

// Re-export types so existing consumers of this module are not broken.
export type {
  Annotation,
  AnnotationBrush,
  AnnotationPolygon,
  AnnotationRect,
  DrawingTool,
  LabelStateAny,
  LabelStateV1,
} from './annotation.types';

@Component({
  selector: 'app-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
})
export class ModalComponent implements OnInit, OnDestroy {
  @Select(FeedbackState.feedbackResponse) feedbackResponse$:
    | Observable<any>
    | undefined;
  @Select(FeedbackState.selectedOptionalColumns) selectedOptionalColumns$:
    | Observable<string[]>
    | undefined;
  @ViewChild('annotationCanvas') annotationCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('theImage') theImageRef!: ElementRef<HTMLImageElement>;
  @ViewChild('imageContainer') imageContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('labelInputRef') labelInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('classSelectRef') classSelectRef?: MatSelect;

  form = new UntypedFormGroup({});
  model = {};
  id = '';
  public rawBlobUrl: string = '';
  public safeUrl: SafeUrl = '';
  dataArray?: any[];
  saveButtondisabled = true;
  currentIndex = 0;
  isZoomed = false;
  public isDragging = false;
  private mouseDownTime = 0;
  private startX = 0;
  private startY = 0;
  private scrollLeft = 0;
  private scrollTop = 0;
  panX = 0;
  panY = 0;
  public zoomLevel = 1;
  private readonly MIN_ZOOM = 1;
  private readonly MAX_ZOOM = 5;
  private readonly ZOOM_STEP = 0.2;
  private readonly LABEL_STATE_KEY = '_labelState';
  private readonly LEGACY_ANNOTATIONS_KEY = '_annotations';
  private initialDataArray?: any[];
  private initialLastRow?: any;
  private initialStateCaptured = false;
  private discardChangesOnClose = false;
  selectedOptionalColumns: string[] = [];

  // Annotation state
  activeTool: DrawingTool = null;
  annotations: Annotation[] = [];
  redoStack: Annotation[] = [];
  private inProgressRect: { startX: number; startY: number; endX: number; endY: number } | null = null;
  private inProgressPolygon: Array<{ x: number; y: number }> | null = null;
  polygonMousePos: { x: number; y: number } | null = null;
  private inProgressBrush: Array<{ x: number; y: number }> | null = null;
  brushCursorPos: { x: number; y: number } | null = null;
  rectCursorPos: { x: number; y: number } | null = null;
  brushSize: number = 10;
  private escapeListener: ((e: KeyboardEvent) => void) | null = null;

  // AI prediction overlay (read-only, not persisted — resets each time the modal opens)
  showAiPredictions = false;
  hoveredAiPrediction: { box: AiPredictionBox; screenPos: { x: number; y: number } } | null = null;

  // Label popup state
  pendingAnnotation: Annotation | null = null;
  pendingAnnotationScreenPos: { x: number; y: number } | null = null;
  labelInput: string = '';
  imageKnownClasses: string[] = [];
  rectKnownClasses: string[] = [];
  polygonBrushKnownClasses: string[] = [];
  imageSessionClasses: string[] = [];
  rectSessionClasses: string[] = [];
  polygonBrushSessionClasses: string[] = [];

  // Edit-points / move-shape state
  private editPointAnnIdx: number = -1;
  private editPointIdx: number = -1;
  private editPointAnchor: { x: number; y: number } | null = null;
  private moveAnnIdx: number = -1;
  private moveStartX: number = 0;
  private moveStartY: number = 0;
  private moveSnapshot: Annotation | null = null;
  hoverAnnIdx: number = -1;
  hoverPointIdx: number = -1;

  // Class color management
  classColors: { [className: string]: string } = {};
  classDescriptions: { [className: string]: string } = {};
  newImageClassInput: string = '';
  newRectClassInput: string = '';
  newPolygonBrushClassInput: string = '';
  allowUserToEditClasses = true;
  hasImageStrategy = false;
  hasPolygonStrategy = false;
  hasBoundingBoxStrategy = false;
  hasLabelingStrategyConfig = false;
  enableRectTool = true;
  enablePolygonTool = true;
  enableBrushTool = true;
  enableEditPointsTool = true;
  enableMoveShapeTool = true;

  get hasAnyAnnotationStrategy(): boolean {
    return this.hasPolygonStrategy || this.hasBoundingBoxStrategy || !this.hasLabelingStrategyConfig;
  }

  get hasClassManager(): boolean {
    return this.hasAnyAnnotationStrategy;
  }

  get hasImageClassManager(): boolean {
    return this.hasImageStrategy;
  }

  get hasRectClassManager(): boolean {
    return this.hasBoundingBoxStrategy || !this.hasLabelingStrategyConfig;
  }

  get hasPolygonBrushClassManager(): boolean {
    return this.hasPolygonStrategy || !this.hasLabelingStrategyConfig;
  }

  get hasAnnotationClassManager(): boolean {
    return this.hasRectClassManager || this.hasPolygonBrushClassManager;
  }

  get hasAnyAnnotationTool(): boolean {
    return (
      this.enableRectTool ||
      this.enablePolygonTool ||
      this.enableBrushTool ||
      this.enableEditPointsTool ||
      this.enableMoveShapeTool
    );
  }

  // Mirrors DataRowStateService.isFilledCompletely but against live in-memory state.
  get isLabelingComplete(): boolean {
    if (!this.hasLabelingStrategyConfig) return false;
    if (this.hasImageStrategy && !(this.model as any)?.['Class']) return false;
    if (this.hasBoundingBoxStrategy && !this.annotations.some(a => a.type === 'rect')) return false;
    if (this.hasPolygonStrategy && !this.annotations.some(a => a.type === 'polygon' || a.type === 'brush')) return false;
    return true;
  }

  toggleZoom(): void {
    if (this.zoomLevel === 1) {
      this.zoomLevel = 2;
      this.isZoomed = true;
    } else {
      this.zoomLevel = 1;
      this.isZoomed = false;
    }
    // Reset pan so click-to-toggle zoom snaps to the natural centered position.
    this.panX = 0;
    this.panY = 0;
    // Refresh canvas layout after the DOM has applied the new transform, then
    // redraw so line-widths and hit-radii reflect the new zoom level.
    requestAnimationFrame(() => {
      this.onImageLoad();
      this.redrawCanvas();
    });
  }

  onScroll(event: WheelEvent, container: HTMLElement): void {
    event.preventDefault();
    const oldZoom = this.zoomLevel;
    const newZoom = event.deltaY < 0
      ? Math.min(this.MAX_ZOOM, +(oldZoom + this.ZOOM_STEP).toFixed(1))
      : Math.max(this.MIN_ZOOM, +(oldZoom - this.ZOOM_STEP).toFixed(1));
    if (newZoom === oldZoom) return;
    const ratio = newZoom / oldZoom;
    const containerRect = container.getBoundingClientRect();
    const cx = event.clientX - containerRect.left;
    const cy = event.clientY - containerRect.top;
    this.zoomLevel = newZoom;
    this.isZoomed = this.zoomLevel > 1;
    this.cdr.detectChanges();
    // Refresh canvas layout and anchor the image pixel that was under the cursor.
    requestAnimationFrame(() => {
      if (newZoom <= this.MIN_ZOOM) {
        // Fully zoomed out: snap back to the natural centered position.
        this.panX = 0;
        this.panY = 0;
      } else {
        // Keep the image pixel under the cursor fixed in screen space.
        // imgRect.left after detectChanges() = containerRect.left + L + panX_old,
        // which is independent of zoom level, so reading it here is correct.
        const imgRect = this.theImageRef.nativeElement.getBoundingClientRect();
        const imgScreenLeft = imgRect.left - containerRect.left;
        const imgScreenTop  = imgRect.top  - containerRect.top;
        this.panX += (cx - imgScreenLeft) * (1 - ratio);
        this.panY += (cy - imgScreenTop)  * (1 - ratio);
      }
      this.cdr.detectChanges();
      this.onImageLoad();
      this.redrawCanvas();
    });
  }

  // ─── Annotation: tool selection ───────────────────────────────────────────

  setActiveTool(tool: DrawingTool): void {
    if (tool && !this.isToolEnabled(tool)) {
      return;
    }
    this.activeTool = this.activeTool === tool ? null : tool;
    this.inProgressRect = null;
    this.inProgressPolygon = null;
    this.polygonMousePos = null;
    this.inProgressBrush = null;
    this.brushCursorPos = null;
    this.editPointAnnIdx = -1;
    this.editPointIdx = -1;
    this.editPointAnchor = null;
    this.moveAnnIdx = -1;
    this.moveSnapshot = null;
    this.hoverAnnIdx = -1;
    this.hoverPointIdx = -1;
    this.redrawCanvas();
  }

  private isToolEnabled(tool: DrawingTool): boolean {
    if (tool === null) {
      return true;
    }
    if (tool === 'rect') {
      return this.enableRectTool;
    }
    if (tool === 'polygon') {
      return this.enablePolygonTool;
    }
    if (tool === 'brush') {
      return this.enableBrushTool;
    }
    if (tool === 'edit-points') {
      return this.enableEditPointsTool;
    }
    if (tool === 'move-shape') {
      return this.enableMoveShapeTool;
    }
    return false;
  }

  get canvasCursor(): string {
    if (!this.activeTool) return 'default';
    if (this.activeTool === 'move-shape') {
      return this.moveAnnIdx !== -1 ? 'grabbing' : (this.hoverAnnIdx !== -1 ? 'move' : 'default');
    }
    if (this.activeTool === 'edit-points') {
      return this.editPointIdx !== -1 ? 'grabbing' : (this.hoverPointIdx !== -1 ? 'grab' : 'crosshair');
    }
    return 'crosshair';
  }

  private getAnnotationCorners(ann: Annotation): Array<{ x: number; y: number }> {
    return ModalCanvasRenderer.getAnnotationCorners(ann);
  }

  private isPointInAnnotation(cx: number, cy: number, ann: Annotation): boolean {
    return ModalCanvasRenderer.isPointInAnnotation(cx, cy, ann);
  }

  // ─── Annotation: image load / canvas setup ────────────────────────────────

  onImageLoad(): void {
    const img = this.theImageRef?.nativeElement;
    const canvas = this.annotationCanvasRef?.nativeElement;
    if (!canvas || !img || !img.naturalWidth) return;

    // Internal canvas resolution = natural image pixels
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // CSS size and position = rendered image dimensions (pre-transform)
    canvas.style.width = img.clientWidth + 'px';
    canvas.style.height = img.clientHeight + 'px';

    // Sub-pixel accurate positioning: getBoundingClientRect() reflects the
    // exact fractional flexbox centering offset, whereas offsetTop/offsetLeft
    // are integer-rounded and can be off by up to 0.5 px.
    const container = this.imageContainerRef.nativeElement;
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    canvas.style.top  = (imgRect.top  - containerRect.top  + container.scrollTop)  + 'px';
    canvas.style.left = (imgRect.left - containerRect.left + container.scrollLeft) + 'px';

    this.redrawCanvas();
  }

  // ─── Annotation: coordinate conversion ───────────────────────────────────

  private getCanvasImageCoords(event: MouseEvent): { x: number; y: number } {
    const canvas    = this.annotationCanvasRef.nativeElement;
    const img       = this.theImageRef.nativeElement;
    const container = this.imageContainerRef.nativeElement;

    // Read fresh layout values on every call — no stale cache from onImageLoad.
    //
    // container  : no CSS transform → getBoundingClientRect() always accurate.
    // imgRect    : with transform-origin: top left the image's left/top edge
    //              never moves when zooming → .left/.top always reflect the
    //              true CSS-layout origin, at any zoom level.
    // clientWidth/Height : layout dimensions, never affected by CSS transforms
    //              → always the pre-zoom rendered size, correct after resize too.
    const containerRect = container.getBoundingClientRect();
    const imgRect       = img.getBoundingClientRect();

    // Image's CSS-layout origin inside the container (scroll-adjusted).
    const imgCssLeft = imgRect.left - containerRect.left + container.scrollLeft;
    const imgCssTop  = imgRect.top  - containerRect.top  + container.scrollTop;

    // Pre-zoom CSS display size of the image.
    const imgCssWidth  = img.clientWidth  || 1;
    const imgCssHeight = img.clientHeight || 1;

    // Mouse position in container-content space.
    const mouseInContainerX = event.clientX - containerRect.left + container.scrollLeft;
    const mouseInContainerY = event.clientY - containerRect.top  + container.scrollTop;

    // Undo CSS transform: scale(zoomLevel) with transform-origin: top left.
    const cssX = (mouseInContainerX - imgCssLeft) / this.zoomLevel;
    const cssY = (mouseInContainerY - imgCssTop)  / this.zoomLevel;

    // CSS pixel position → natural image pixel coordinates.
    // Clamp to image bounds so drawing can start/continue exactly at the edge,
    // even when the pointer is slightly outside the image/canvas.
    const x = cssX * (canvas.width  / imgCssWidth);
    const y = cssY * (canvas.height / imgCssHeight);
    return {
      x: Math.min(Math.max(x, 0), canvas.width),
      y: Math.min(Math.max(y, 0), canvas.height),
    };
  }

  onImageContainerMouseDown(event: MouseEvent, container: HTMLElement): void {
    ModalMouseHandlers.onImageContainerMouseDown(this as any, event, container);
  }

  onImageContainerMouseMove(event: MouseEvent, container: HTMLElement): void {
    this.updateAiPredictionHover(event);
    ModalMouseHandlers.onImageContainerMouseMove(this as any, event, container);
  }

  onImageContainerMouseUp(event: MouseEvent): void {
    ModalMouseHandlers.onImageContainerMouseUp(this as any, event);
  }

  // ─── AI prediction overlay (read-only) ───────────────────────────────────

  get aiPredictionBoxes(): AiPredictionBox[] {
    return getAiPredictionBoxes(this.dataArray?.[this.currentIndex]);
  }

  get hasAiPredictions(): boolean {
    return this.aiPredictionBoxes.length > 0;
  }

  toggleAiPredictionsVisibility(): void {
    this.showAiPredictions = !this.showAiPredictions;
    this.hoveredAiPrediction = null;
    this.redrawCanvas();
  }

  onAiPredictionAreaMouseLeave(): void {
    this.hoveredAiPrediction = null;
  }

  private updateAiPredictionHover(event: MouseEvent): void {
    if (!this.showAiPredictions) {
      if (this.hoveredAiPrediction) this.hoveredAiPrediction = null;
      return;
    }
    const boxes = this.aiPredictionBoxes;
    if (boxes.length === 0) {
      if (this.hoveredAiPrediction) this.hoveredAiPrediction = null;
      return;
    }
    const { x, y } = this.getCanvasImageCoords(event);
    const box = boxes.find(b => x >= b.x1 && x <= b.x2 && y >= b.y1 && y <= b.y2);
    if (!box) {
      if (this.hoveredAiPrediction) this.hoveredAiPrediction = null;
      return;
    }
    const container = this.imageContainerRef.nativeElement;
    const containerRect = container.getBoundingClientRect();
    this.hoveredAiPrediction = {
      box,
      screenPos: {
        x: Math.min(event.clientX - containerRect.left + 12, container.clientWidth - 220),
        y: Math.min(event.clientY - containerRect.top + 12, container.clientHeight - 210),
      },
    };
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.onImageLoad();
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    ModalMouseHandlers.onWindowMouseMove(this as any, event);
  }

  @HostListener('window:mouseup', ['$event'])
  onWindowMouseUp(event: MouseEvent): void {
    ModalMouseHandlers.onWindowMouseUp(this as any, event);
  }

  // ─── Annotation: canvas mouse events ─────────────────────────────────────

  onCanvasMouseDown(event: MouseEvent): void {
    ModalMouseHandlers.onCanvasMouseDown(this as any, event);
  }

  onCanvasMouseMove(event: MouseEvent): void {
    ModalMouseHandlers.onCanvasMouseMove(this as any, event);
  }

  onCanvasMouseUp(event: MouseEvent): void {
    ModalMouseHandlers.onCanvasMouseUp(this as any, event);
  }

  onCanvasDblClick(event: MouseEvent): void {
    ModalMouseHandlers.onCanvasDblClick(this as any, event);
  }

  onCanvasMouseLeave(): void {
    ModalMouseHandlers.onCanvasMouseLeave(this as any);
  }

  // ─── Annotation: label popup ──────────────────────────────────────────────

  openLabelPopup(annotation: Annotation): void {
    // Keep new geometry in a pending state until the user confirms a label.
    this.pendingAnnotation = annotation;
    this.pendingAnnotationScreenPos = this.getLabelAnchorPos(annotation);
    this.labelInput = '';
    this.cdr.detectChanges();
    if (this.allowUserToEditClasses) {
      setTimeout(() => this.labelInputRef?.nativeElement?.focus(), 50);
    } else {
      setTimeout(() => this.classSelectRef?.open(), 50);
    }
  }

  private getLabelAnchorPos(annotation: Annotation): { x: number; y: number } {
    const canvas = this.annotationCanvasRef.nativeElement;
    const img = this.theImageRef.nativeElement;
    const container = this.imageContainerRef.nativeElement;
    let anchorPx: number, anchorPy: number;
    if (annotation.type === 'rect') {
      anchorPx = annotation.x + annotation.width;
      anchorPy = annotation.y + annotation.height;
    } else {
      const last = annotation.points[annotation.points.length - 1];
      anchorPx = last.x;
      anchorPy = last.y;
    }
    // Convert natural canvas pixels → pre-zoom CSS pixels.
    const imgCssWidth  = img.clientWidth  || 1;
    const imgCssHeight = img.clientHeight || 1;
    const cssPxX = canvas.width  > 0 ? (anchorPx / canvas.width)  * imgCssWidth  : anchorPx;
    const cssPxY = canvas.height > 0 ? (anchorPy / canvas.height) * imgCssHeight : anchorPy;
    // Use getBoundingClientRect so the position accounts for the full CSS transform
    // (translate + scale), giving the correct result at any zoom level and pan offset.
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const imgLeft = imgRect.left - containerRect.left;
    const imgTop  = imgRect.top  - containerRect.top;
    const x = imgLeft + cssPxX * this.zoomLevel;
    const y = imgTop  + cssPxY * this.zoomLevel;
    return {
      // Keep popup inside the visible container area.
      x: Math.min(x, container.clientWidth - 260),
      y: Math.min(y, container.clientHeight - 70),
    };
  }

  confirmLabel(): void {
    if (!this.pendingAnnotation) return;
    const label = this.labelInput.trim();
    if (!this.allowUserToEditClasses) {
      const validClasses = this.pendingAnnotation?.type === 'rect' ? this.rectClasses : this.polygonBrushClasses;
      if (!label || !validClasses.includes(label)) {
        return;
      }
    }
    this.pendingAnnotation.label = label || undefined;
    if (label) {
      this.pendingAnnotation.color = this.getClassColor(label);
      if (this.allowUserToEditClasses && !this.annotationClasses.includes(label)) {
        // New user-entered labels become session classes immediately.
        if (this.pendingAnnotation?.type === 'rect') {
          this.rectSessionClasses.push(label);
        } else {
          this.polygonBrushSessionClasses.push(label);
        }
      }
    }
    this.annotations.push(this.pendingAnnotation);
    this.redoStack = [];
    console.log('[Annotation] Label confirmed:', { ...this.pendingAnnotation });
    this.pendingAnnotation = null;
    this.pendingAnnotationScreenPos = null;
    this.labelInput = '';
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  cancelLabel(): void {
    this.pendingAnnotation = null;
    this.pendingAnnotationScreenPos = null;
    this.labelInput = '';
    this.redrawCanvas();
  }

  private saveAnnotationsToCurrentItem(): void {
    if (!this.dataArray) return;
    const item = this.dataArray[this.currentIndex];
    if (item) {
      this.ensureLabelStateForItem(item, 'manual');
      if (!this.lastRow) this.lastRow = {};
      this.lastRow['_imageSessionClasses'] = [...this.imageSessionClasses];
      this.lastRow['_rectSessionClasses'] = [...this.rectSessionClasses];
      this.lastRow['_polygonBrushSessionClasses'] = [...this.polygonBrushSessionClasses];
      this.lastRow['_classColors'] = { ...this.classColors };
      // Persist into NGXS state so annotations/classes survive modal close/reopen.
      this.feedbackResponseEmitter?.emit({
        dataframe: this.dataArray,
        lastrow: this.lastRow,
      });
    }
  }

  get filteredAnnotationClasses(): string[] {
    const all = this.pendingAnnotation?.type === 'rect' ? this.rectClasses : this.polygonBrushClasses;
    const q = this.labelInput.toLowerCase().trim();
    return q ? all.filter(c => c.toLowerCase().includes(q)) : all;
  }

  get pendingAnnotationClasses(): string[] {
    return this.pendingAnnotation?.type === 'rect' ? this.rectClasses : this.polygonBrushClasses;
  }

  get imageClasses(): string[] {
    return [...new Set([...this.imageKnownClasses, ...this.imageSessionClasses])];
  }

  get imageClassesUseTwoColumns(): boolean {
    return this.imageClasses.every(cls => cls.length <= 15);
  }

  get rectClasses(): string[] {
    return [...new Set([...this.rectKnownClasses, ...this.rectSessionClasses])];
  }

  get polygonBrushClasses(): string[] {
    return [...new Set([...this.polygonBrushKnownClasses, ...this.polygonBrushSessionClasses])];
  }

  get annotationClasses(): string[] {
    return [...new Set([...this.rectClasses, ...this.polygonBrushClasses])];
  }

  getClassesForAnnotation(ann: Annotation): string[] {
    return ann.type === 'rect' ? this.rectClasses : this.polygonBrushClasses;
  }

  isAnnotationVisible(ann: Annotation): boolean {
    return ann.isVisibleOnCanvas !== false;
  }

  isAnnotationClassNameVisible(ann: Annotation): boolean {
    return ann.isCanvasLabelVisible !== false;
  }

  toggleAnnotationVisibility(index: number): void {
    const ann = this.annotations[index];
    if (!ann) {
      return;
    }
    ann.isVisibleOnCanvas = ann.isVisibleOnCanvas === false;
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  toggleAnnotationClassNameVisibility(index: number): void {
    const ann = this.annotations[index];
    if (!ann) {
      return;
    }
    ann.isCanvasLabelVisible = ann.isCanvasLabelVisible === false;
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  areAllAnnotationsVisible(): boolean {
    return this.annotations.length > 0 && this.annotations.every((ann) => this.isAnnotationVisible(ann));
  }

  areAllAnnotationsHidden(): boolean {
    return this.annotations.length > 0 && this.annotations.every((ann) => !this.isAnnotationVisible(ann));
  }

  areAllAnnotationClassNamesVisible(): boolean {
    return this.annotations.length > 0 && this.annotations.every((ann) => this.isAnnotationClassNameVisible(ann));
  }

  areAllAnnotationClassNamesHidden(): boolean {
    return this.annotations.length > 0 && this.annotations.every((ann) => !this.isAnnotationClassNameVisible(ann));
  }

  toggleAllAnnotationsVisibility(): void {
    if (this.annotations.length === 0) {
      return;
    }
    const nextVisible = !this.areAllAnnotationsVisible();
    for (const ann of this.annotations) {
      ann.isVisibleOnCanvas = nextVisible;
    }
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  toggleAllAnnotationClassNameVisibility(): void {
    if (this.annotations.length === 0) {
      return;
    }
    const nextVisible = !this.areAllAnnotationClassNamesVisible();
    for (const ann of this.annotations) {
      ann.isCanvasLabelVisible = nextVisible;
    }
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  getClassColor(label: string): string {
    return ModalClassManager.getClassColor(this as any, label);
  }

  getClassInfoTooltip(label: string): string {
    const description = (this.classDescriptions[label] ?? '').trim();
    return description || label;
  }

  private hexToRgba(hex: string, alpha: number): string {
    return ModalCanvasRenderer.hexToRgba(hex, alpha);
  }

  addImageClass(): void {
    if (!this.allowUserToEditClasses) {
      return;
    }
    const name = this.newImageClassInput.trim();
    if (!name || this.imageClasses.includes(name)) { return; }
    this.imageSessionClasses.push(name);
    this.newImageClassInput = '';
    this.updateImageClassFieldOptions();
    this.saveClassesToState();
  }

  addRectClass(): void {
    if (!this.allowUserToEditClasses) { return; }
    const name = this.newRectClassInput.trim();
    if (!name || this.rectClasses.includes(name)) { return; }
    this.getClassColor(name);
    this.rectSessionClasses.push(name);
    this.newRectClassInput = '';
    this.saveClassesToState();
  }

  addPolygonBrushClass(): void {
    if (!this.allowUserToEditClasses) { return; }
    const name = this.newPolygonBrushClassInput.trim();
    if (!name || this.polygonBrushClasses.includes(name)) { return; }
    this.getClassColor(name);
    this.polygonBrushSessionClasses.push(name);
    this.newPolygonBrushClassInput = '';
    this.saveClassesToState();
  }

  removeImageClass(cls: string): void {
    if (!this.allowUserToEditClasses) {
      return;
    }
    this.imageSessionClasses = this.imageSessionClasses.filter(c => c !== cls);
    this.imageKnownClasses = this.imageKnownClasses.filter(c => c !== cls);
    this.updateImageClassFieldOptions();
    this.saveClassesToState();
  }

  removeRectClass(cls: string): void {
    if (!this.allowUserToEditClasses) { return; }
    this.rectSessionClasses = this.rectSessionClasses.filter(c => c !== cls);
    this.rectKnownClasses = this.rectKnownClasses.filter(c => c !== cls);
    this.annotations = this.annotations.filter(a => !(a.type === 'rect' && a.label === cls));
    if (this.dataArray) {
      for (const item of this.dataArray) {
        if (item[this.LEGACY_ANNOTATIONS_KEY]) {
          item[this.LEGACY_ANNOTATIONS_KEY] = item[this.LEGACY_ANNOTATIONS_KEY].filter(
            (a: any) => !(a.type === 'rect' && a.label === cls)
          );
        }
        if (item[this.LABEL_STATE_KEY]?.annotations?.items) {
          item[this.LABEL_STATE_KEY].annotations.items = item[this.LABEL_STATE_KEY].annotations.items
            .filter((a: any) => !(a.type === 'rect' && a.label === cls));
          item[this.LABEL_STATE_KEY].annotations.stats = this.buildAnnotationStats(item[this.LABEL_STATE_KEY].annotations.items);
        }
      }
    }
    this.redrawCanvas();
    this.saveClassesToState();
  }

  removePolygonBrushClass(cls: string): void {
    if (!this.allowUserToEditClasses) { return; }
    this.polygonBrushSessionClasses = this.polygonBrushSessionClasses.filter(c => c !== cls);
    this.polygonBrushKnownClasses = this.polygonBrushKnownClasses.filter(c => c !== cls);
    this.annotations = this.annotations.filter(
      a => !((a.type === 'polygon' || a.type === 'brush') && a.label === cls)
    );
    if (this.dataArray) {
      for (const item of this.dataArray) {
        if (item[this.LEGACY_ANNOTATIONS_KEY]) {
          item[this.LEGACY_ANNOTATIONS_KEY] = item[this.LEGACY_ANNOTATIONS_KEY].filter(
            (a: any) => !((a.type === 'polygon' || a.type === 'brush') && a.label === cls)
          );
        }
        if (item[this.LABEL_STATE_KEY]?.annotations?.items) {
          item[this.LABEL_STATE_KEY].annotations.items = item[this.LABEL_STATE_KEY].annotations.items
            .filter((a: any) => !((a.type === 'polygon' || a.type === 'brush') && a.label === cls));
          item[this.LABEL_STATE_KEY].annotations.stats = this.buildAnnotationStats(item[this.LABEL_STATE_KEY].annotations.items);
        }
      }
    }
    this.redrawCanvas();
    this.saveClassesToState();
  }

  private updateImageClassFieldOptions(): void {
    ModalClassManager.updateImageClassFieldOptions(this as any);
  }

  removeAnnotation(index: number): void {
    this.annotations.splice(index, 1);
    this.redoStack = [];
    if (this.editingAnnotationIdx === index) {
      this.editingAnnotationIdx = -1;
    } else if (this.editingAnnotationIdx > index) {
      this.editingAnnotationIdx--;
    }
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  editingAnnotationIdx: number = -1;
  @ViewChildren('annotationClassSelect') annotationClassSelects!: QueryList<MatSelect>;

  toggleEditAnnotation(index: number): void {
    this.editingAnnotationIdx = this.editingAnnotationIdx === index ? -1 : index;
    if (this.editingAnnotationIdx !== -1) {
      this.cdr.detectChanges();
      this.annotationClassSelects.first?.open();
    }
  }

  changeAnnotationClass(index: number, newLabel: string): void {
    const ann = this.annotations[index];
    if (!ann) return;
    ann.label = newLabel || undefined;
    ann.color = newLabel ? this.getClassColor(newLabel) : undefined;
    this.editingAnnotationIdx = -1;
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  private saveClassesToState(): void {
    const item = this.dataArray?.[this.currentIndex];
    if (item) {
      this.ensureLabelStateForItem(item, 'manual');
    }
    this.syncClassesIntoExistingLabelStates();
    if (!this.lastRow) this.lastRow = {};
    this.lastRow['_imageSessionClasses'] = [...this.imageSessionClasses];
    this.lastRow['_rectSessionClasses'] = [...this.rectSessionClasses];
    this.lastRow['_polygonBrushSessionClasses'] = [...this.polygonBrushSessionClasses];
    this.lastRow['_classColors'] = { ...this.classColors };
    this.feedbackResponseEmitter?.emit({
      dataframe: this.dataArray,
      lastrow: this.lastRow,
    });
  }

  private syncClassesIntoExistingLabelStates(): void {
    ModalClassManager.syncClassesIntoExistingLabelStates(this as any, this.LABEL_STATE_KEY);
  }

  // ─── Annotation: undo / clear ─────────────────────────────────────────────

  undoAnnotation(): void {
    if (this.inProgressPolygon) {
      if (this.inProgressPolygon.length > 1) {
        this.inProgressPolygon.pop();
      } else {
        this.inProgressPolygon = null;
        this.polygonMousePos = null;
      }
    } else {
      const removed = this.annotations.pop();
      if (removed !== undefined) {
        this.redoStack.push(removed);
      }
      this.saveAnnotationsToCurrentItem();
    }
    this.redrawCanvas();
  }

  redoAnnotation(): void {
    if (this.redoStack.length === 0) return;
    const annotation = this.redoStack.pop()!;
    this.annotations.push(annotation);
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  clearAnnotations(): void {
    this.annotations = [];
    this.redoStack = [];
    this.inProgressRect = null;
    this.inProgressPolygon = null;
    this.polygonMousePos = null;
    this.inProgressBrush = null;
    this.pendingAnnotation = null;
    this.pendingAnnotationScreenPos = null;
    this.labelInput = '';
    this.saveAnnotationsToCurrentItem();
    this.redrawCanvas();
  }

  // ─── Annotation: canvas rendering ────────────────────────────────────────

  redrawCanvas(): void {
    ModalCanvasRenderer.redrawCanvas(this as any);
  }

  /**
   * Repositions the annotation canvas to match the current image position
   * without resizing it (avoids the expensive canvas buffer reset on every pan).
   */
  repositionCanvas(): void {
    const canvas = this.annotationCanvasRef?.nativeElement;
    const img = this.theImageRef?.nativeElement;
    if (!canvas || !img) return;
    const container = this.imageContainerRef.nativeElement;
    const imgRect = img.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    canvas.style.top  = (imgRect.top  - containerRect.top)  + 'px';
    canvas.style.left = (imgRect.left - containerRect.left) + 'px';
    this.redrawCanvas();
  }

  private getBrushHalfWidth(): number {
    return ModalCanvasRenderer.getBrushHalfWidth(this as any);
  }

  private stateInitialized = false;
  private hasFlushedOnClose = false;

  @Emitter(FeedbackState.feedbackResponseEmitter) feedbackResponseEmitter:
    | Emittable<any>
    | undefined;

  fields: FormlyFieldConfig[] = [
    {
      fieldGroup: [],
    },
  ];
  lastRow: any;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  private readLabelingInformation(item?: any): any {
    const fromDialog = this.data?.['labeling_information'] ?? this.data?.['structure']?.['labeling_information'];
    if (fromDialog !== undefined) {
      return fromDialog;
    }
    const fromItem = item?.[this.LABEL_STATE_KEY]?.labelingInformation;
    if (fromItem !== undefined) {
      return fromItem;
    }
    const currentItem = this.dataArray?.[this.currentIndex];
    return currentItem?.[this.LABEL_STATE_KEY]?.labelingInformation;
  }

  private deepClone<T>(value: T): T {
    return ModalLabelState.deepClone(value);
  }

  private parseS3Url(imagePath: string): { bucket?: string; key?: string } {
    return ModalLabelState.parseS3Url(imagePath);
  }

  private getFormValue(raw: any): LabelStateFormValue | undefined {
    return ModalLabelState.getFormValue(raw);
  }

  private buildFormStateFromItem(item: any, preferModelValues: boolean = true): LabelStateV1['form'] {
    return ModalLabelState.buildFormStateFromItem(this as any, item, preferModelValues);
  }

  private buildAnnotationStats(items: Annotation[]): LabelStateV1['annotations']['stats'] {
    return ModalLabelState.buildAnnotationStats(items);
  }

  private mergeUniqueStrings(base: string[] = [], incoming: string[] = []): string[] {
    return ModalLabelState.mergeUniqueStrings(base, incoming);
  }

  private ensureLabelStateForItem(
    item: any,
    source: LabelStateV1['audit']['source'] = 'manual',
    preferModelValues: boolean = true
  ): LabelStateV1 {
    return ModalLabelState.ensureLabelStateForItem(
      this as any,
      item,
      source,
      preferModelValues,
      this.LABEL_STATE_KEY,
      this.LEGACY_ANNOTATIONS_KEY
    );
  }

  private getPersistedFormFieldValue(item: any, key: string): LabelStateFormValue | undefined {
    return ModalLabelState.getPersistedFormFieldValue(item, key, this.LABEL_STATE_KEY);
  }

  private flushStateBeforeClose(persistFormSelection: boolean = false): void {
    if (this.hasFlushedOnClose) {
      return;
    }
    this.hasFlushedOnClose = true;
    if (!this.dataArray) {
      return;
    }

    const currentItem = this.dataArray[this.currentIndex];
    if (currentItem) {
      let targetItem = currentItem;
      if (persistFormSelection) {
        // Ensure select values from model are written back into item objects.
        this.changeRawValue(currentItem);
        targetItem = this.dataArray[this.currentIndex];
      }
      this.ensureLabelStateForItem(targetItem, 'manual', true);
    }

    this.syncClassesIntoExistingLabelStates();

    if (!this.lastRow) {
      this.lastRow = {};
    }
    this.lastRow['_imageSessionClasses'] = [...this.imageSessionClasses];
    this.lastRow['_rectSessionClasses'] = [...this.rectSessionClasses];
    this.lastRow['_polygonBrushSessionClasses'] = [...this.polygonBrushSessionClasses];
    this.lastRow['_classColors'] = { ...this.classColors };

    this.feedbackResponseEmitter?.emit({
      dataframe: this.dataArray,
      lastrow: this.lastRow,
    });
  }

  private getPersistedAnnotations(item: any): Annotation[] {
    return ModalLabelState.getPersistedAnnotations(item, this.LABEL_STATE_KEY, this.LEGACY_ANNOTATIONS_KEY);
  }

  private hydrateClassesFromLabelState(item: any): void {
    ModalLabelState.hydrateClassesFromLabelState(this as any, item, this.LABEL_STATE_KEY);
  }

  private getSelectOptionLabelsFromFields(): string[] {
    const classSet = new Set<string>();
    for (const field of (this.fields[0].fieldGroup ?? [])) {
      const opts = (field as any).templateOptions?.options;
      if (!Array.isArray(opts)) {
        continue;
      }
      for (const opt of opts) {
        if (opt?.label) {
          classSet.add(opt.label);
        }
      }
    }
    return [...classSet];
  }

  private applyLabelingConfiguration(): void {
    const labelingInformation = this.readLabelingInformation();
    const strategies = Array.isArray(labelingInformation?.labeling_strategies)
      ? labelingInformation.labeling_strategies
      : [];
    const hasStrategies = strategies.length > 0;
    this.hasLabelingStrategyConfig = hasStrategies;

    this.allowUserToEditClasses = labelingInformation?.allow_user_to_edit_classes !== false;
    this.hasImageStrategy = false;
    this.hasPolygonStrategy = false;
    this.hasBoundingBoxStrategy = false;
    this.classDescriptions = {};

    const imageClassSet = new Set<string>();
    const rectClassSet = new Set<string>();
    const polygonBrushClassSet = new Set<string>();

    for (const strategyData of strategies) {
      const strategy = strategyData?.labeling_strategy;
      const classes = Array.isArray(strategyData?.classes) ? strategyData.classes : [];

      if (strategy === 'image') {
        this.hasImageStrategy = true;
      } else if (strategy === 'polygon') {
        this.hasPolygonStrategy = true;
      } else if (strategy === 'bounding_box') {
        this.hasBoundingBoxStrategy = true;
      }

      if (strategy === 'image' || strategy === 'polygon' || strategy === 'bounding_box') {
        for (const cls of classes) {
          const name = (cls?.name ?? '').trim();
          if (!name) {
            continue;
          }
          if (strategy === 'image') {
            imageClassSet.add(name);
          } else if (strategy === 'bounding_box') {
            rectClassSet.add(name);
          } else if (strategy === 'polygon') {
            polygonBrushClassSet.add(name);
          }
          // Optional strategy metadata can pre-seed UI appearance and tooltips.
          if (cls?.color) {
            this.classColors[name] = cls.color;
          } else if (strategy === 'bounding_box' || strategy === 'polygon') {
            console.error(`[LabelingConfig] Class "${name}" in strategy "${strategy}" is missing a color. The backend should always provide one.`);
          }
          const description = (cls?.description ?? '').trim();
          if (description) {
            this.classDescriptions[name] = description;
          }
        }
      }
    }

    if (hasStrategies) {
      this.enableRectTool = this.hasBoundingBoxStrategy;
      this.enablePolygonTool = this.hasPolygonStrategy;
      this.enableBrushTool = this.hasPolygonStrategy;
      this.enableEditPointsTool = this.hasPolygonStrategy || this.hasBoundingBoxStrategy;
      this.enableMoveShapeTool = this.hasPolygonStrategy || this.hasBoundingBoxStrategy;
      this.imageKnownClasses = [...imageClassSet];
      this.rectKnownClasses = [...rectClassSet];
      this.polygonBrushKnownClasses = [...polygonBrushClassSet];
      for (const cls of [...this.rectKnownClasses, ...this.polygonBrushKnownClasses]) {
        this.getClassColor(cls);
      }
      return;
    }

    // Backward-compatible fallback for old responses without labeling_information.
    this.enableRectTool = true;
    this.enablePolygonTool = true;
    this.enableBrushTool = true;
    this.enableEditPointsTool = true;
    this.enableMoveShapeTool = true;
    this.imageKnownClasses = this.getSelectOptionLabelsFromFields();
    this.rectKnownClasses = [...this.imageKnownClasses];
    this.polygonBrushKnownClasses = [...this.imageKnownClasses];
    for (const cls of [...this.rectKnownClasses, ...this.polygonBrushKnownClasses]) {
      this.getClassColor(cls);
    }
  }



  onNoClick(): void {
    this.discardChangesOnClose = true;
    if (this.initialStateCaptured && this.dataArray && this.initialDataArray) {
      this.dataArray.splice(
        0,
        this.dataArray.length,
        ...ModalLabelState.deepClone(this.initialDataArray)
      );
      if (this.initialLastRow) {
        this.lastRow = ModalLabelState.deepClone(this.initialLastRow);
      }
      this.feedbackResponseEmitter?.emit({
        dataframe: this.dataArray,
        lastrow: this.lastRow,
      });
    }
    this.dialogRef.close();
  }

  downloadImage(): void {
    if (!this.rawBlobUrl) {
      console.error('❌ No image loaded yet');
      return;
    }
    const a = document.createElement('a');
    a.href = this.rawBlobUrl;
    a.download = this.data?.filename || this.data?.imagePath?.split('/').pop() || 'image.png';
    a.click();
  }

  get hasPrevious(): boolean {
    return this.currentIndex > 0;
  }

  get hasNext(): boolean {
    return !!this.dataArray && this.currentIndex < this.dataArray.length - 1;
  }

  navigatePrevious(): void {
    if (this.hasPrevious) {
      this.loadItem(this.currentIndex - 1);
    }
  }

  navigateNext(): void {
    if (this.hasNext) {
      this.loadItem(this.currentIndex + 1);
    }
  }

  onSelected() {
    this.saveButtondisabled = !this.form.valid;
  }

  onImageClassSelected(value: string): void {
    (this.model as any)['Class'] = value;
    // rAF fires after MatSelect's synchronous _restoreFocus(), preventing arrow-key drift
    requestAnimationFrame(() => (document.activeElement as HTMLElement)?.blur());
  }

  clearDropdowns() {
    const result = clearLabelsState({
      fields: this.fields,
      model: this.model,
      dataArray: this.dataArray,
      id: this.id,
      data: this.data?.['row'],
      labelStateKey: this.LABEL_STATE_KEY,
      legacyAnnotationsKey: this.LEGACY_ANNOTATIONS_KEY,
      clearAnnotations: true,
    });
    this.model = result.model;
    delete (this.model as any)['Class'];

    this.annotations = [];
    this.redoStack = [];
    this.inProgressRect = null;
    this.inProgressPolygon = null;
    this.polygonMousePos = null;
    this.inProgressBrush = null;
    this.pendingAnnotation = null;
    this.pendingAnnotationScreenPos = null;
    this.labelInput = '';

    const item = result.currentItem ?? this.dataArray?.[this.currentIndex] ?? (this.getItem() as any);
    this.clearPersistedImageClass(item);
    this.clearPersistedImageClass(this.data?.['row']);
    if (item) {
      // Rebuild persisted state after clearing to keep storage and UI aligned.
      this.ensureLabelStateForItem(item, 'manual');
    }

    this.updateImageClassFieldOptions();
    this.saveButtondisabled = true;
    this.redrawCanvas();
    this.feedbackResponseEmitter?.emit({
      dataframe: this.dataArray,
      lastrow: this.lastRow,
    });
  }

  private clearPersistedImageClass(item: any): void {
    if (!item) return;

    if (item['Class'] && typeof item['Class'] === 'object') {
      const { value, ...rest } = item['Class'];
      void value;
      item['Class'] = rest;
    }

    delete item[this.LABEL_STATE_KEY]?.form?.values?.['Class'];
  }

  save() {
    if (this.dataArray) {
      const currentItem = this.dataArray[this.currentIndex];
      if (currentItem) {
        currentItem['_savedByUser'] = true;
      }
    }
    this.flushStateBeforeClose(false);
    this.dialogRef.close({ annotations: this.annotations });
  }

  getItem() {
    let result = {};
    if (this.dataArray) {
      for (const item of this.dataArray) {
        if (item['id']) {
          if (item['id']['key'] == this.id) {
            result = item;
            break;
          }
        }
      }
    }
    return result;
  }

  changeRawValue(item: any) {
    const mergedItem: any = {};

    // Rebuild item, preserving select structures with the selected value embedded
    for (const [key, val] of Object.entries(this.model as any)) {
      const original = item[key];
      if (original && typeof original === 'object' && original['type'] === 'select') {
        mergedItem[key] = { ...original, value: val };
      } else {
        mergedItem[key] = val;
      }
    }

    // Preserve Photo, id, ai_case, _annotations from original item
    const field = 'Photo';
    const id = 'id';
    const aiCase = 'ai_case';
    mergedItem[field] = item[field as keyof typeof item];
    mergedItem[id] = item[id as keyof typeof item];
    mergedItem[aiCase] = item[aiCase as keyof typeof item];
    mergedItem[this.LEGACY_ANNOTATIONS_KEY] = item[this.LEGACY_ANNOTATIONS_KEY];
    mergedItem[this.LABEL_STATE_KEY] = item[this.LABEL_STATE_KEY];

    if (this.dataArray) {
      this.dataArray.splice(this.dataArray.indexOf(item), 1, mergedItem);
      this.ensureLabelStateForItem(mergedItem, 'manual');
    }
  }

  downloadmage() {
    console.log('url*', this.data['imagePath']);
    this.loadImageFromPath(this.data['imagePath']);
  }

  private loadImageFromPath(imagePath: string): void {
    ModalImageLoader.loadImageFromPath(this as any, imagePath);
  }

  private loadItem(index: number): void {
    // Save annotations of the image we are leaving
    this.saveAnnotationsToCurrentItem();

    const item = this.dataArray![index];
    this.currentIndex = index;
    this.id = item['id']['key'];

    // Reset zoom and pan per image so navigation always starts from a stable viewport.
    this.zoomLevel = 1;
    this.isZoomed = false;
    this.panX = 0;
    this.panY = 0;

    // Restore persisted annotations for the target image, or start fresh.
    this.annotations = this.getPersistedAnnotations(item);
    this.redoStack = [];
    this.hydrateClassesFromLabelState(item);
    this.inProgressRect = null;
    this.inProgressPolygon = null;
    this.polygonMousePos = null;
    this.pendingAnnotation = null;
    this.pendingAnnotationScreenPos = null;
    this.labelInput = '';

    // Reset image so spinner shows while the new one loads
    this.rawBlobUrl = '';
    this.safeUrl = '';
    this.loadImageFromPath(item['Photo']['link']);

    // Rebuild form and model from clean state before applying row-specific defaults.
    this.form = new UntypedFormGroup({});
    this.model = {};

    // Rebuild the field list from this item's own data so it reflects only the
    // columns it actually has, instead of reusing whichever row opened the modal.
    const fieldGroup: FormlyFieldConfig[] = buildModalInfoFields(item, this.selectedOptionalColumns);
    this.fields = [{ fieldGroup }];

    // Re-apply labeling config to add dynamic fields (like Class) and update known classes
    this.applyLabelingConfiguration();
    this.updateImageClassFieldOptions();

    // Now patch per-row defaultValues for all fields
    for (const fieldConfig of (this.fields[0].fieldGroup ?? [])) {
      const key = fieldConfig.key as string;
      const itemValue = this.getPersistedFormFieldValue(item, key);
      if (itemValue === undefined || itemValue === null) { continue; }
      if (typeof itemValue === 'string' || typeof itemValue === 'number' || typeof itemValue === 'boolean') {
        fieldConfig.defaultValue = itemValue;
      }
    }

    // Restore image class selection directly into model (not managed by Formly)
    if (this.hasImageStrategy) {
      const classValue = this.getPersistedFormFieldValue(item, 'Class');
      if (typeof classValue === 'string' && classValue) {
        (this.model as any)['Class'] = classValue;
      }
    }

    // Determine green border state
    const requiredFields = (this.fields[0].fieldGroup ?? []).filter((f: any) => f.templateOptions?.required);
    const allPrefilled =
      requiredFields.length > 0 &&
      requiredFields.every((f: any) => f.defaultValue !== undefined);
    this.saveButtondisabled = !allPrefilled;

    this.cdr.detectChanges();

  }

  ngOnInit() {
    this.dialogRef.beforeClosed().subscribe(() => {
      if (!this.discardChangesOnClose) {
        this.flushStateBeforeClose(false);
      }
    });

    this.escapeListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.pendingAnnotation) {
          this.cancelLabel();
          return;
        }
        this.inProgressPolygon = null;
        this.polygonMousePos = null;
        this.inProgressRect = null;
        this.redrawCanvas();
      } else if (e.key === 'Enter') {
        ModalMouseHandlers.onEnterFinishPolygon(this as any);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'mat-select') return;
        if (this.pendingAnnotation) return;
        e.preventDefault();
        if (e.key === 'ArrowLeft') this.navigatePrevious();
        else this.navigateNext();
      }
    };
    document.addEventListener('keydown', this.escapeListener);

    this.selectedOptionalColumns$?.subscribe((columns) => {
      this.selectedOptionalColumns = columns ?? [];
    });

    this.id = this.data['id'];
    this.downloadmage();
    this.fields[0].fieldGroup = buildModalInfoFields(this.data['row'], this.selectedOptionalColumns);
    this.applyLabelingConfiguration();

    // Show green border immediately if all required fields already have a saved defaultValue
    const requiredFields = (this.fields[0].fieldGroup ?? []).filter(
      (f: any) => f.templateOptions?.required
    );
    const allPrefilled =
      requiredFields.length > 0 &&
      requiredFields.every((f: any) => f.defaultValue !== undefined);
    this.saveButtondisabled = !allPrefilled;
    this.feedbackResponse$?.subscribe((res) => {
      if (res) {
        this.dataArray = JSON.parse(JSON.stringify(res.dataframe));
        if (!this.initialStateCaptured) {
          this.initialDataArray = ModalLabelState.deepClone(this.dataArray);
          this.initialLastRow = ModalLabelState.deepClone(res.lastrow ?? {});
          this.initialStateCaptured = true;
        }
        if (this.currentIndex === 0 && this.id) {
          // Sync currentIndex to the actual position of the opened item
          const idx = (this.dataArray ?? []).findIndex((item: any) => item['id']?.['key'] === this.id);
          if (idx !== -1) { this.currentIndex = idx; }
        }
        if (res.lastrow) {
          this.lastRow = JSON.parse(JSON.stringify(res.lastrow));
          if (this.lastRow['_imageSessionClasses']) {
            this.imageSessionClasses = [...this.lastRow['_imageSessionClasses']];
          } else if (this.lastRow['_sessionClasses']) {
            this.imageSessionClasses = [...this.lastRow['_sessionClasses']];
          }
          if (this.lastRow['_rectSessionClasses']) {
            this.rectSessionClasses = [...this.lastRow['_rectSessionClasses']];
          }
          if (this.lastRow['_polygonBrushSessionClasses']) {
            this.polygonBrushSessionClasses = [...this.lastRow['_polygonBrushSessionClasses']];
          }
          if (!this.lastRow['_rectSessionClasses'] && !this.lastRow['_polygonBrushSessionClasses']) {
            // Backward compat: distribute old combined _annotationSessionClasses to both
            const legacy = this.lastRow['_annotationSessionClasses'] ?? this.lastRow['_sessionClasses'] ?? [];
            this.rectSessionClasses = [...legacy];
            this.polygonBrushSessionClasses = [...legacy];
          }
          if (this.lastRow['_classColors']) {
            this.classColors = { ...this.lastRow['_classColors'] };
          }
          // Update Class field options with restored session classes
          this.updateImageClassFieldOptions();
          // Trigger change detection after restoring classes
          this.cdr.detectChanges();
        }
        // Restore annotations for the current image after state refresh
        const currentItem = this.dataArray?.[this.currentIndex];
        this.annotations = this.getPersistedAnnotations(currentItem);
        this.hydrateClassesFromLabelState(currentItem);
        this.updateImageClassFieldOptions();
        this.redrawCanvas();

        // On first state load, patch the form's defaultValues with saved field values from current item
        if (!this.stateInitialized) {
          this.stateInitialized = true;
          // Patch defaultValues for all Formly fields
          for (const fieldConfig of (this.fields[0].fieldGroup ?? [])) {
            const key = fieldConfig.key as string;
            const itemValue = this.getPersistedFormFieldValue(currentItem, key);
            if (itemValue === undefined || itemValue === null) { continue; }
            if (typeof itemValue === 'string' || typeof itemValue === 'number' || typeof itemValue === 'boolean') {
              fieldConfig.defaultValue = itemValue;
            }
          }
          // Restore image class selection directly into model
          if (this.hasImageStrategy) {
            const classValue = this.getPersistedFormFieldValue(currentItem, 'Class');
            if (typeof classValue === 'string' && classValue) {
              (this.model as any)['Class'] = classValue;
            }
          }
          // Update save button state based on required fields
          const initRequiredFields = (this.fields[0].fieldGroup ?? []).filter((f: any) => f.templateOptions?.required);
          this.saveButtondisabled = !(
            initRequiredFields.length > 0 &&
            initRequiredFields.every((f: any) => f.defaultValue !== undefined)
          );
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (!this.discardChangesOnClose) {
      this.flushStateBeforeClose(false);
    }
    if (this.escapeListener) {
      document.removeEventListener('keydown', this.escapeListener);
    }
  }
}